import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { Account, AccountSignature } from "@/api/opb/opbTypes";
import { slugs } from "@/router/slugs";
import { getFullPath } from "@/router/utils/getFullPath";
import { FormPaymentMethodType, ZuoraCreditCardTypes } from "../types";
import { observeHostedPaymentFrame } from "../utils/observe-hosted-payment-frame";

interface ZuoraPaymentPageProps {
  accountSignature?: AccountSignature;
  account?: Account;
  isDefaultPaymentChecked?: boolean;
  callbackData?: object;
  redirectUrl?: string;
  defaultFormData?: object;
}

export function ZuoraPaymentPage({
  accountSignature,
  account,
  isDefaultPaymentChecked = false,
  redirectUrl,
  callbackData,
  defaultFormData,
}: ZuoraPaymentPageProps) {
  const [searchParams] = useSearchParams();
  const container = useRef<HTMLDivElement>(null);
  const base = import.meta.env.VITE_CONTENT_ROOT;
  const pathname = getFullPath(slugs.paymentMethods);

  function successCallback(response: any) {
    searchParams.set("success", response.success);
    searchParams.set("AuthorizeResult", response.AuthorizeResult);
    searchParams.set("zThreeDs2TxId", response.zThreeDs2TxId);
    searchParams.set("ThreeDSResult", response.ThreeDSResult);
    searchParams.set("responseFrom", response.responseFrom);
    searchParams.set("token", response.token);
    searchParams.set("nextStep", response.nextStep);
    searchParams.set("refId", response.refId);
    searchParams.set("signature", response.signature);
    searchParams.set("tenantId", response.tenantId);
    searchParams.set("redirectUrl", response.redirectUrl);
    searchParams.set("field_passthrough1", response.field_passthrough1);
    searchParams.set("field_passthrough2", response.field_passthrough2);
    searchParams.set("field_passthrough3", response.field_passthrough3);
    searchParams.set("field_passthrough4", response.field_passthrough4);
    searchParams.set("field_passthrough5", response.field_passthrough5);
    searchParams.set("field_passthrough6", response.field_passthrough6);
    searchParams.set("field_passthrough7", response.field_passthrough7);
    searchParams.set("field_passthrough8", response.field_passthrough8);
    searchParams.set("field_passthrough9", response.field_passthrough9);
    searchParams.set("field_passthrough10", response.field_passthrough10);
    searchParams.set("field_passthrough11", response.field_passthrough11);
    searchParams.set("field_passthrough12", response.field_passthrough12);
    searchParams.set("field_passthrough13", response.field_passthrough13);
    searchParams.set("field_passthrough14", response.field_passthrough14);
    searchParams.set("field_passthrough15", response.field_passthrough15);

    // Breaking out of iframe and redirecting the parent window to the payment methods page
    window.parent.location.href = `${base}${pathname}?${searchParams}`;
  }

  function errorCallback(key: string, code: string, message: string) {
    // Need to recreate the searchParams object because on error,
    // Zuora just returns an error message
    Sentry.captureException(new Error(message));

    searchParams.set("success", "false");
    searchParams.set("responseFrom", "Response_From_Submit_Page");
    searchParams.set("field_passthrough1", account?._id ?? "");
    searchParams.set("field_passthrough2", FormPaymentMethodType.CREDIT_CARD);
    searchParams.set("field_passthrough5", redirectUrl ?? "");
    searchParams.set("field_passthrough6", JSON.stringify(callbackData));

    window.parent.location.href = `${base}${pathname}?${searchParams}`;
  }

  const params = {
    field_accountId: account?.zuoraId,
    field_passthrough1: account?._id,
    field_passthrough2: FormPaymentMethodType.CREDIT_CARD,
    field_passthrough3: isDefaultPaymentChecked,
    field_passthrough5: redirectUrl ?? "",
    field_passthrough6: JSON.stringify(callbackData),
    field_passthrough7: true,
    id: accountSignature?.paymentPageId,
    key: accountSignature?.publicKey,
    locale: "en",
    tenantId: accountSignature?.tenantId,
    token: accountSignature?.token,
    signature: accountSignature?.signature,
    param_supportedTypes: Object.values(ZuoraCreditCardTypes).join(","),
    paymentGateway: account?.paymentGateway,
    submitEnabled: "true",
    style: "inline",
    url: accountSignature?.paymentPageUrl,
  };

  useEffect(() => {
    const root = container.current;
    if (root == null) return undefined;
    const stopObserving = observeHostedPaymentFrame(root);
    window.Z.renderWithErrorHandler(
      params,
      defaultFormData,
      successCallback,
      errorCallback
    );
    return stopObserving;
  }, [accountSignature]);

  return (
    <div ref={container} id="zuora_payment" className="payment-secure-form" />
  );
}
